import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  ChatBotState,
  ChatMessage,
  ChatActionStatus,
  ChatActionRequest,
  MachineContext,
  ActionResultPayload,
  ThreadBuffer,
  POPUP_THREAD_ID,
  DEFAULT_MODEL_ID,
  MODEL_OVERRIDE_IDS,
  ModelTurnStats,
} from "./models/chatBotModels";
import chatBotApi from "./ChatBotApi";
// MODEL-PICKER: `.cc`-only UX gate for model_override resolution. Pure
// hostname check, no deps — safe static import from shared → customer
// (this file already reaches into customer/midas via dynamic imports).
import { isCcDevSurface } from "../../customer/midas/components/v3/ccSurface";
// V3-SEGMENT-STREAMING (SP-2): pure helpers (no React/Redux) — safe static
// imports from shared → customer, same pattern as `ccSurface` above.
// `countMarkerLines` backs the marker-vs-frame dedupe; `classifyLoopProgressFrame`
// normalizes a loop_progress frame into a render action.
import { countMarkerLines } from "../../customer/midas/components/v3/markerBlocksV3";
import { classifyLoopProgressFrame } from "../../customer/midas/components/v3/loopProgressV3";
import i18n from "../../core/i18n";
import {
  readSSEStream,
  mapBotSources,
  mapBotArtifacts,
  mapBotActions,
} from "./streamBotMessage";
import accountContext from "../../account/accountContext";
// MIDAS-401-SILENT-EXPIRY: the app's existing post-logout navigation helper
// (same one `core/errorHandler.ts` and the header account menu use).
import { buildAccountSignInWithReturn } from "../../account/routes";
import { Roles } from "../../account/models/AccountModels";
import { isMidasPilotUser } from "../../customer/midas/pilotThreadScope";

/** Sentinel origin for the MidasPilot scope guard's rejection. Deliberately NOT a real
 *  thread id and NOT POPUP_THREAD_ID: the rejected reducer then finds no buffer to write
 *  an error into and skips the "mark last user message failed" popup branch, making the
 *  abort a clean no-op. Mirrors the existing "draft-guard" sentinel. */
const PILOT_SCOPE_GUARD_ORIGIN = "pilot-scope-guard";

/**
 * MIDAS-BUG-001 (Part 1): Build a meaningful optimistic thread title.
 * - If user typed text, use first 40 chars.
 * - If only an image was attached, prefer the filename; fall back to
 *   a localised "Image" label (SK: "Obrázok", CS: "Obrázek", EN: "Image").
 * - Last-resort fallback keeps the legacy "New Chat" string so we never
 *   write an empty title.
 */
const buildOptimisticTitle = (message: string, image?: File): string => {
  const trimmed = (message || "").slice(0, 40);
  if (trimmed) return trimmed;
  if (image) {
    const name = (image.name || "").trim();
    if (name) return name.slice(0, 40);
    return i18n.t("chatBot:imageOnlyChat", "Image");
  }
  return "New Chat";
};

/** Generate a unique message ID using crypto.randomUUID or fallback */
const generateId = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * MIDAS-404-SILENCE (bot bug report 2026-08-04): the chat backend's ownership
 * gate returns a deliberate 404 "no-disclosure" response for a machine that is
 * not in the session's ownership list (e.g. a machine added to the platform
 * after the session — and its ownership snapshot — was built). Previously that
 * 404 landed in an orphaned thread buffer and the MIDAS chat rendered NOTHING
 * (total silence). Detect the status and map it to a NEUTRAL, no-disclosure
 * user message. The HTTP status is surfaced two ways: ChatBotApi.streamMessage
 * throws an Error carrying `.status`; axios (sync fallback) exposes
 * `err.response.status`.
 */
const isNotFoundStatus = (err: unknown): boolean => {
  const direct = (err as { status?: number } | null)?.status;
  const viaResponse = (err as { response?: { status?: number } } | null)
    ?.response?.status;
  return direct === 404 || viaResponse === 404;
};

/** Neutral, ownership-non-disclosing message for the 404 ownership-gate path. */
const machineUnavailableMessage = (): string =>
  i18n.t(
    "chatBot:midas.machineUnavailableInSession",
    "This machine isn't available in this session — please reopen the chat."
  );

/**
 * MIDAS-401-SILENT-EXPIRY: our JWT is 4h absolute from login and non-sliding,
 * and bot-proxy URLs are deliberately carved OUT of the global 401 -> logout
 * axios interceptor in `core/api.ts` (the LOGOUT-AMPLIFIER fix of 2026-04-30 —
 * that carve-out is correct and stays). Consequence: a `/midas` tab left open
 * past the 4h mark looks completely normal, and the first send renders the raw
 * untranslated string "Stream request failed with status 401" in the error box.
 * Detect the status HERE instead — same dual read as `isNotFoundStatus`, since
 * ChatBotApi.streamMessage throws an Error carrying `.status` while axios (sync
 * fallback) exposes `err.response.status`.
 */
const isUnauthorizedStatus = (err: unknown): boolean => {
  const direct = (err as { status?: number } | null)?.status;
  const viaResponse = (err as { response?: { status?: number } } | null)
    ?.response?.status;
  return direct === 401 || viaResponse === 401;
};

/** Plain-language "your session ended" message for the 401 path. */
const sessionExpiredMessage = (): string =>
  i18n.t(
    "chatBot:midas.sessionExpired",
    "Your session has expired — please sign in again."
  );

/**
 * Delay before the session-expiry logout fires. The message MUST be readable:
 * an instant redirect wipes the screen before the user can see why, which is
 * worse than the silent failure it replaces. 5s is long enough to read one
 * line twice, short enough that the redirect is obviously caused by it.
 */
const SESSION_EXPIRED_LOGOUT_DELAY_MS = 5000;

/** Once-per-page-load guard — a burst of failing sends must not stack timers. */
let sessionExpiredLogoutScheduled = false;

/**
 * Trigger the app's EXISTING logout path so the user can actually recover:
 * `accountContext.logout()` is the same call `core/api.ts` makes on a non-bot
 * 401, and `buildAccountSignInWithReturn` is the same post-logout navigation
 * `core/errorHandler.ts` and the header account menu already use — so after
 * signing in the user lands back on the page they were on (`/midas`).
 * NOT a new auth mechanism; deliberately does NOT touch refresh tokens.
 */
const scheduleSessionExpiredLogout = (): void => {
  if (sessionExpiredLogoutScheduled) return;
  sessionExpiredLogoutScheduled = true;
  window.setTimeout(() => {
    accountContext.logout();
    const returnUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.href = buildAccountSignInWithReturn(returnUrl);
  }, SESSION_EXPIRED_LOGOUT_DELAY_MS);
};

/**
 * Build the user-facing error string for a failed send. A 404 (ownership gate)
 * always becomes the neutral message; a 401 (expired MachineID session) becomes
 * the session-expired message and schedules the logout; anything else keeps its
 * original text.
 * Accepts an optional second error so the caller can prefer the ORIGINAL
 * stream failure (the ownership gate) over a secondary sync-fallback failure.
 */
const deriveSendErrorMessage = (
  primaryErr: unknown,
  fallback: string,
  secondaryErr?: unknown
): string => {
  if (isNotFoundStatus(primaryErr) || isNotFoundStatus(secondaryErr)) {
    return machineUnavailableMessage();
  }
  // MIDAS-401-SILENT-EXPIRY: additive sibling of the 404 branch above.
  // Checks BOTH errors for the same reason the 404 one does — the send can
  // fail on the stream leg and again on the sync fallback, and either may
  // carry the 401. Also schedules the logout so the user can get back in.
  if (isUnauthorizedStatus(primaryErr) || isUnauthorizedStatus(secondaryErr)) {
    scheduleSessionExpiredLogout();
    return sessionExpiredMessage();
  }
  return primaryErr instanceof Error ? primaryErr.message : fallback;
};

// ============================================================
// Async Thunks
// ============================================================

/** Send a message via SSE streaming with progress indicators */
export const sendMessage = createAsyncThunk(
  "chatBotSlice/sendMessage",
  async (
    { message, machineId, threadId, image, actionId, actionContext, actionResult, originThreadId: callerOriginThreadId, displayText, pageInfoRequest, modelOverride }: {
      message: string;
      machineId?: string;
      threadId?: string;
      image?: File;
      actionId?: string;
      actionContext?: Record<string, unknown>;
      /** MA-0b.3: Action result to report back to bot for conversation continuity */
      actionResult?: ActionResultPayload;
      /** MIDAS-SCOPESTREAM: The originating thread ID for scoped streaming.
       *  MIDAS passes activeThreadId; popup passes POPUP_THREAD_ID.
       *  Frozen for the lifetime of the thunk. */
      originThreadId?: string;
      /** MC-26: Override the text shown in the chat bubble / persisted to history.
       *  The full `message` is still sent to the bot as the stream body.
       *  Used by the (i) info button so users see a friendly short prompt
       *  ("Describe page 46 — Jaw control") instead of the raw info_prompt. */
      displayText?: string;
      /** MC-27: Structured metadata sent only by (i) button clicks on diagram_overlay
       *  artifacts. Lets the bot skip intent classification + vector search and fetch
       *  the exact Qdrant page directly. Omitted for normal chat messages. */
      pageInfoRequest?: {
        page: number;
        collection: string;
        list_id?: string;
      };
      /** MODEL-PICKER (spec §5): explicit per-message model override. When
       *  OMITTED, the thunk resolves the thread's selection from
       *  `midasV3.modelOverrideByThread` AT DISPATCH TIME (covering every
       *  send path — composer, follow-up chips, regenerate, action results —
       *  per §4A point 6: re-attached on EVERY outgoing turn). Must be a
       *  `ModelOverrideId` allowlist value; never free text. */
      modelOverride?: string;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    // Hoisted for access in both try and catch blocks (MIDAS-THREADSYNC Phase 2)
    let isNewThread = false;
    let optimisticTempId: string | null = null;
    let effectiveMachineId: string | null = null;

    // MIDAS-SCOPESTREAM: Resolve the originating thread ID.
    // This is the canonical thread ID for this stream. It starts as the caller-provided
    // value and may be updated by reconciliation (temp→real ID, popup→real ID).
    let originThreadId = callerOriginThreadId || POPUP_THREAD_ID;

    // BUG-2026-03-23: Mutable ref that the jitter buffer interval reads on every tick.
    // JavaScript closures capture variables by reference, so a `let` would work, but using
    // an explicit ref object makes the mutation pattern auditable — the jitter buffer reads
    // `streamTarget.current` and ONLY reconciliation code writes to it. This prevents
    // accidental token bleed if originThreadId is mutated by unrelated code.
    const streamTarget = { current: originThreadId };

    // Stream ID for activeStreams tracking — hoisted so the catch block can access it.
    let streamId = "";

    // Top-level try/catch to guarantee isLoading is always reset via rejectWithValue
    try {
      // MODEL-PICKER P3: is this send an ATTACHED-COLUMN send? Attached
      // columns are always EXISTING threads and pass their own threadId as
      // originThreadId. Column sends must not consume the newThreadPending
      // flag (that belongs to the PRIMARY chat's next send) and must not
      // inherit the global machineContext.
      const rootState0 = getState() as any;
      const attachColumns0: Array<{ threadId: string }> =
        rootState0.midasAttach?.columns ?? [];
      const isColumnSend =
        !!callerOriginThreadId &&
        callerOriginThreadId !== POPUP_THREAD_ID &&
        attachColumns0.some((c) => c.threadId === callerOriginThreadId);

      const chatState = (getState() as { chatBot: ChatBotState }).chatBot;

      // ── TAJMAC-PILOT SCOPE INVARIANT (MIDAS_PILOT_ROLE_SPEC.md §5) ──
      //
      // A MidasPilot must never produce an unscoped ("platform" / fleet-wide) chat.
      // This lives HERE, at the thunk entry, because guarding the composer cannot win:
      // twelve call sites dispatch this thunk (composer ×2, follow-up chips, regenerate,
      // artifact describe/info buttons ×4 across V1/V2/V3, action results, action
      // buttons, popup panel), most passing `machineId: undefined` and relying on the
      // machineContext fallback below. Every future button would re-open the hole.
      //
      // Mirrors the resolution used for `effectiveMachineId` further down (explicit arg
      // wins; the primary composer falls back to the global machineContext, column sends
      // never do). Keep the two in step if that resolution ever changes.
      //
      // Aborts BEFORE the newThreadPending flag is consumed and before any optimistic
      // thread row, network request or stream is created — nothing to unwind.
      //
      // The rejection is a deliberate clean no-op: `sendMessage.pending` only clears
      // `state.error`, and the sentinel originThreadId has no entry in `threadBuffers`,
      // so the rejected reducer writes no error text, marks no message as failed (it is
      // not POPUP_THREAD_ID) and sets no global error (surfaceGlobalError omitted).
      // Verified no dispatch site calls `.unwrap()` on this thunk, so nothing throws.
      // Same shape as the existing MODEL-PICKER §6 draft guard below.
      //
      // Layering (all three intentional, do not collapse): this is the GUARANTEE;
      // ChatInputAreaV3's `pilotScopeBlocked` early-return + disabled composer are the
      // UX layer; MidasV3Layout's auto-scope is the convenience that means neither
      // normally fires. Non-pilot sends short-circuit on the role check and are
      // byte-identical.
      if (accountContext.user?.role === Roles.MIDAS_PILOT) {
        const pilotMachineId = isColumnSend
          ? machineId || null
          : machineId || chatState.machineContext?.machineId || null;
        if (!pilotMachineId) {
          return rejectWithValue({
            error: "Machine scope required",
            originThreadId: PILOT_SCOPE_GUARD_ORIGIN,
            streamId: "",
          });
        }
      }

      // Check and consume newThreadPending flag — tells gateway to start a fresh thread
      isNewThread = chatState.newThreadPending && !isColumnSend;
      if (isNewThread) {
        dispatch(clearNewThreadFlag());
      }

      // ── MODEL-PICKER §6 HARD GUARD (belt to the UI affordance) ──
      // A brand-new PRIMARY draft's first turn goes out WITHOUT thread_id
      // (`new_thread: true`; the real id only arrives via `thread_created`).
      // A no-thread_id message falls back to the bot's SHARED user-level
      // session where machine context WOULD bleed across columns (bot Q(f)).
      // Therefore a draft send and an attached-column stream must NEVER
      // overlap. The composers are disabled via selectors; this is the
      // race-window backstop.
      if (attachColumns0.length > 0) {
        const buffers0 = chatState.threadBuffers || {};
        const anyColumnStreaming = attachColumns0.some(
          (c) => buffers0[c.threadId]?.isLoading === true
        );
        const optimisticThreads: Array<{ id: string; isOptimistic?: boolean }> =
          rootState0.midasPage?.threads ?? [];
        const draftInFlight = optimisticThreads.some(
          (t) => t.isOptimistic === true && buffers0[t.id]?.isLoading === true
        );
        if (isNewThread && anyColumnStreaming) {
          // Note: originThreadId would default to POPUP here (draft sends
          // pass no origin) and the rejected reducer's popup branch would
          // spuriously mark the PREVIOUS user message as failed — use a
          // sentinel key instead (no buffer exists for it → clean no-op).
          return rejectWithValue({
            error: "Waiting for attached chats to finish streaming",
            originThreadId: "draft-guard",
            streamId: "",
          });
        }
        if (isColumnSend && draftInFlight) {
          return rejectWithValue({
            error: "Waiting for the new chat to be created",
            originThreadId,
            streamId: "",
          });
        }
      }

      // ── MODEL-PICKER (spec §5 / §4A point 6): resolve the thread's model
      // AT DISPATCH TIME. Explicit arg wins; otherwise read the per-thread
      // client map (the bot has NO thread stickiness — the field must ride
      // EVERY outgoing turn of a thread with a non-default selection).
      // Popup sends never carry an override (no picker on that surface).
      // Default model = send NOTHING (never ""). `.cc`-gated: off-`.cc` the
      // picker doesn't render AND resolution is skipped (belt + braces —
      // the server allowlist is the real boundary).
      let effectiveModelOverride: string | undefined = modelOverride;
      if (
        effectiveModelOverride === undefined &&
        callerOriginThreadId !== POPUP_THREAD_ID &&
        isCcDevSurface()
      ) {
        const modelMap: Record<string, string> =
          rootState0.midasV3?.modelOverrideByThread ?? {};
        const modelKey = threadId || "draft";
        const selected = modelMap[modelKey];
        if (
          typeof selected === "string" &&
          (MODEL_OVERRIDE_IDS as readonly string[]).includes(selected)
        ) {
          effectiveModelOverride = selected;
        }
      }
      if (effectiveModelOverride === DEFAULT_MODEL_ID) {
        effectiveModelOverride = undefined; // default = absent field
      }

      // BUG 11 (owner ruling 2026-10-02, option B): NO `pipeline_override`
      // is resolved or sent — from any surface. The bot gateway defaults every
      // turn to its v3 pipeline when the field is absent. The per-thread
      // v1/v2 opt-out state is gone (midasV3Slice), and this thunk reads NO
      // pipeline state, so even a stale legacy map can never reach the wire.
      //
      // An "override turn" (sync-fallback disabled below) is any turn that
      // carries model_override: the sync POST /v1/bot/chat ignores it, so a
      // fallback would silently answer with the default model while the
      // thread's picker says otherwise.
      const isOverrideTurn = !!effectiveModelOverride;

      // BUG-2026-03-20 FIX: Ensure machine_id is always sent when a machine context is active.
      // The caller passes machineId from a useCallback closure, but if there's a timing issue
      // (e.g. context set after the callback was last captured), fall back to Redux state.
      // MODEL-PICKER P3: column sends pass their machineId EXPLICITLY and must
      // NOT fall back to the global machineContext — that belongs to the
      // PRIMARY chat and would bleed the wrong machine into a column turn.
      effectiveMachineId = isColumnSend
        ? machineId || null
        : machineId || chatState.machineContext?.machineId || null;

      // MIDAS-THREADSYNC Phase 2: Track whether we received thread_id from the SSE answer
      let receivedThreadId: string | null = null;
      // MIDAS-SSE-THREAD: Flag to prevent double reconciliation when thread_created fires early
      let earlyReconciled = false;

      // MIDAS-THREADSYNC Phase 2: Insert optimistic thread entry immediately on send
      if (isNewThread) {
        optimisticTempId = generateId();
        // MIDAS-SCOPESTREAM: Use the optimistic temp ID as the origin thread ID for new threads.
        // Check the CALLER's value (callerOriginThreadId), not the resolved value (originThreadId),
        // because the popup explicitly passes POPUP_THREAD_ID while MIDAS passes undefined for new threads.
        // Without this, MIDAS new-thread buffers are keyed under "popup" while the component reads
        // from optimisticTempId (set by addOptimisticThread) — causing streaming to never appear.
        if (callerOriginThreadId !== POPUP_THREAD_ID) {
          originThreadId = optimisticTempId;
          streamTarget.current = optimisticTempId;
        }
        const optimisticTitle = buildOptimisticTitle(message, image);
        try {
          const { addOptimisticThread } = await import(
            "../../customer/midas/midasPageSlice"
          );
          dispatch(addOptimisticThread({
            tempId: optimisticTempId,
            title: optimisticTitle,
            machineId: effectiveMachineId,
          }));
        } catch {
          // Non-critical — sidebar will sync on refetch
        }
      }

      // MIDAS-SCOPESTREAM: Register the streaming buffer for this origin thread.
      // Uses a local stream ID (not meta.requestId which is unavailable inside the thunk body).
      streamId = generateId();
      dispatch(registerStreamBuffer({ threadId: originThreadId, requestId: streamId }));

      // Helper: fallback refetch for sidebar thread lists (Phase 1 safety net).
      // Only used when thread_id is absent from the SSE answer event.
      const refreshSidebarFallback = () => {
        if (!isNewThread) return;
        setTimeout(async () => {
          try {
            const { fetchMachineThreads, fetchThreadCounts } = await import(
              "../../customer/midas/midasPageSlice"
            );
            dispatch(fetchMachineThreads(effectiveMachineId));
            const rootState = getState() as any;
            const machinesMap = rootState?.customerMachinery?.machines;
            if (machinesMap && typeof machinesMap === "object") {
              const ids: string[] = [];
              for (const group of Object.values(machinesMap)) {
                if (Array.isArray(group)) {
                  for (const m of group as any[]) {
                    if (m.id) ids.push(m.id);
                  }
                }
              }
              if (ids.length > 0) {
                dispatch(fetchThreadCounts(ids));
              }
            }
          } catch {
            // Non-critical — sidebar will sync on next page load
          }
        }, 2000);
      };

      // Helper: reconcile optimistic thread with real thread_id, or fall back to refetch
      const reconcileOrRefetch = () => {
        if (!isNewThread) return;
        if (earlyReconciled) return; // thread_created already handled reconciliation
        if (receivedThreadId && optimisticTempId) {
          // Phase 2: Reconcile optimistic entry with real ID
          import("../../customer/midas/midasPageSlice").then(
            ({ reconcileOptimisticThread }) => {
              dispatch(reconcileOptimisticThread({
                tempId: optimisticTempId!,
                realId: receivedThreadId!,
              }));
            }
          ).catch(() => {});
          // MIDAS-SCOPESTREAM: Re-key thread buffer from optimistic temp ID to real thread ID
          dispatch(reconcileStreamOrigin({
            requestId: streamId,
            oldThreadId: optimisticTempId!,
            newThreadId: receivedThreadId!,
          }));
          originThreadId = receivedThreadId!;
          streamTarget.current = receivedThreadId!;
        } else {
          // Phase 1 fallback: thread_id missing — refetch after delay
          refreshSidebarFallback();
        }
      };

      // Create blob URL for user-attached image (for rendering in chat)
      let imageUrl: string | undefined;
      let imageName: string | undefined;
      if (image) {
        imageUrl = URL.createObjectURL(image);
        imageName = image.name;
      }

      // MODEL-PICKER P3: deliver a message to its attached column (when the
      // origin is / still is an attached column). Returns true when the
      // message was consumed by a column — the fulfilled reducer then clears
      // the buffer WITHOUT storing pendingBotMessage (dedup: a later
      // loadThread of that thread must not double-append the answer).
      // Re-reads state at CALL time so a detach mid-flight naturally falls
      // back to the normal background-stream pendingBotMessage path.
      const deliverToColumnIfAttached = async (
        msg: ChatMessage
      ): Promise<boolean> => {
        if (!isColumnSend || !callerOriginThreadId) return false;
        const colsNow: Array<{ threadId: string }> =
          (getState() as any).midasAttach?.columns ?? [];
        if (!colsNow.some((c) => c.threadId === callerOriginThreadId)) {
          return false;
        }
        try {
          const { appendColumnMessage } = await import(
            "../../customer/midas/midasAttachSlice"
          );
          dispatch(
            appendColumnMessage({
              threadId: callerOriginThreadId,
              message: msg,
            })
          );
          return true;
        } catch {
          return false;
        }
      };

      // Add the user message to state immediately (skip for action clicks — empty message).
      // MC-26: If displayText is set (e.g. from the (i) info button), store that as
      // the visible / persisted bubble text instead of the raw stream body.
      // MODEL-PICKER P3: attached-column sends append to THAT column's
      // messages array instead of the global (primary) messages — otherwise
      // the user bubble would pollute the primary thread (cross-contamination).
      if (message || image) {
        const userMessage: ChatMessage = {
          id: generateId(),
          role: "user",
          text: displayText && displayText.length > 0 ? displayText : message,
          timestamp: new Date().toISOString(),
          imageUrl,
          imageName,
        };
        const consumedByColumn = await deliverToColumnIfAttached(userMessage);
        if (!consumedByColumn) {
          dispatch(addMessage(userMessage));
        }
      }

      let response: Response;
      try {
        response = await chatBotApi.streamMessage(message, effectiveMachineId, threadId, image, actionId, actionContext, actionResult, isNewThread, displayText, pageInfoRequest, effectiveModelOverride);
      } catch (streamSetupErr: unknown) {
        // MODEL-PICKER (spec §5, bot-endorsed): the sync POST /v1/bot/chat
        // does NOT honor model_override (silently ignored → DEFAULT model
        // answers with no error and no echo). Falling back would silently
        // put a mislabeled default-model answer inside a thread whose picker
        // says otherwise — worse than an honest, retryable error in a
        // model-evaluation tool. So: override turns NEVER fall back to sync.
        if (isOverrideTurn) {
          if (isNewThread && optimisticTempId) {
            import("../../customer/midas/midasPageSlice").then(
              ({ removeOptimisticThread }) => {
                dispatch(removeOptimisticThread({ tempId: optimisticTempId!, machineId: effectiveMachineId }));
              }
            ).catch(() => {});
          }
          const errMsg = deriveSendErrorMessage(
            streamSetupErr,
            "Failed to send message"
          );
          return rejectWithValue({
            error: errMsg,
            originThreadId,
            streamId,
            surfaceGlobalError: isNewThread,
          });
        }
        // SSE streaming failed — fall back to synchronous endpoint
        // (non-override turns only; model_override is NEVER sent here).
        try {
          const syncResponse = await chatBotApi.sendMessage(message, effectiveMachineId, threadId, image, actionId, actionContext, actionResult, isNewThread);
          const botMessage: ChatMessage = {
            id: generateId(),
            role: "assistant",
            text: syncResponse.message,
            timestamp: new Date().toISOString(),
            sources: mapBotSources(syncResponse.sources),
            suggestions: syncResponse.suggestions,
            intent: syncResponse.metadata?.intent,
            artifacts: mapBotArtifacts((syncResponse as any).artifacts),
            actions: mapBotActions((syncResponse as any).actions),
          };
          // SSE failed, sync succeeded — check for thread_id
          if ((syncResponse as any).thread_id && typeof (syncResponse as any).thread_id === "string") {
            receivedThreadId = (syncResponse as any).thread_id;
          }
          reconcileOrRefetch();
          const deliveredToColumn = await deliverToColumnIfAttached(botMessage);
          return { botMessage, originThreadId, streamId, deliveredToColumn };
        } catch (syncErr: unknown) {
          // Both SSE and sync failed — remove optimistic entry
          if (isNewThread && optimisticTempId) {
            import("../../customer/midas/midasPageSlice").then(
              ({ removeOptimisticThread }) => {
                dispatch(removeOptimisticThread({ tempId: optimisticTempId!, machineId: effectiveMachineId }));
              }
            ).catch(() => {});
          }
          // MIDAS-404-SILENCE: prefer the ORIGINAL stream failure
          // (streamSetupErr = the ownership gate's 404) when deriving the
          // neutral message; the sync fallback typically 404s too.
          const errMsg = deriveSendErrorMessage(
            streamSetupErr,
            "Failed to send message",
            syncErr
          );
          return rejectWithValue({
            error: errMsg,
            originThreadId,
            streamId,
            surfaceGlobalError: isNewThread,
          });
        }
      }

      // Read the SSE stream using shared utility
      let streamError: string | null = null;
      let pendingActionRequest: import("./models/chatBotModels").ChatActionRequest | null = null;
      const pendingUiActions: import("./models/chatBotModels").ChatActionRequest[] = [];
      // MODEL-PICKER-11 (§14.2/§14.3): accumulate this turn's thinking deltas
      // + capture its (single) stats frame so both attach to the completed
      // bot message. The buffer's live thinkingText copy drives the streaming
      // panel and clears with streamingText; the message copy keeps the panel
      // re-expandable while the turn is on screen.
      let thinkingAccum = "";
      let turnStats: ModelTurnStats | null = null;

      // Jitter buffer: accumulate tokens in a character queue and release
      // at a constant rate to smooth out bursty SSE delivery.
      // Like a VoIP jitter buffer — absorbs network burst patterns and
      // outputs a steady character stream for flicker-free rendering.
      let charQueue = "";
      let releaseIntervalId: ReturnType<typeof setInterval> | null = null;
      let initialDelayTimerId: ReturnType<typeof setTimeout> | null = null;
      let initialDelayDone = false;
      const INITIAL_DELAY = 300;   // ms before first character renders (builds buffer)
      const RELEASE_INTERVAL = 30; // ms between character releases
      const CHARS_PER_TICK = 3;    // characters released per tick

      const enqueueToken = (text: string) => {
        if (!text) return;
        charQueue += text;

        // Start the release loop on first token (with initial delay)
        if (!releaseIntervalId && !initialDelayDone) {
          initialDelayTimerId = setTimeout(() => {
            initialDelayDone = true;
            initialDelayTimerId = null;
            releaseIntervalId = setInterval(() => {
              if (charQueue.length === 0) {
                // Queue empty — keep interval running, more tokens may arrive
                return;
              }
              const chunk = charQueue.slice(0, CHARS_PER_TICK);
              charQueue = charQueue.slice(CHARS_PER_TICK);
              // BUG-2026-03-23: Read from streamTarget.current (explicit ref) instead of
              // the `let originThreadId` directly. Both point to the same value, but the ref
              // pattern ensures only reconciliation code can update the dispatch target.
              dispatch(appendStreamingToken({ text: chunk, threadId: streamTarget.current }));
            }, RELEASE_INTERVAL);
          }, INITIAL_DELAY);
        }
      };

      const flushQueue = () => {
        if (initialDelayTimerId !== null) {
          clearTimeout(initialDelayTimerId);
          initialDelayTimerId = null;
        }
        if (releaseIntervalId) {
          clearInterval(releaseIntervalId);
          releaseIntervalId = null;
        }
        if (charQueue.length > 0) {
          // BUG-2026-03-23: Read from streamTarget.current — same rationale as enqueueToken
          dispatch(appendStreamingToken({ text: charQueue, threadId: streamTarget.current }));
          charQueue = "";
        }
      };

      // V3-SEGMENT-STREAMING (SP-2): drain the queued chars immediately WITHOUT
      // tearing down the release interval — so any token text queued before a
      // `loop_progress` frame lands in streamingText BEFORE the frame's row
      // update (wire-order, §10.2), while subsequent tokens keep streaming
      // smoothly (a plain `flushQueue()` here would stop the interval and, since
      // `initialDelayDone` is already true, it would never restart → later
      // segments would stall until the next frame / stream end).
      const flushQueueImmediate = () => {
        if (charQueue.length > 0) {
          dispatch(appendStreamingToken({ text: charQueue, threadId: streamTarget.current }));
          charQueue = "";
        }
      };

      const botMessage = await readSSEStream(response, {
        onStep: (stepKey) => dispatch(setCurrentStep({ text: stepKey, threadId: streamTarget.current })),
        onMessage: () => {
          // Messages are returned from readSSEStream, not via callback
        },
        onError: (errMsg) => {
          streamError = errMsg;
          // Clean up jitter buffer on error
          flushQueue();
        },
        onActionRequest: (actionRequest) => {
          // Dispatch rendering based on approval_tier (not action_type prefix)
          // Tier 0: chip rendering (nav.switch_machine, nav.switch_platform, future quick actions)
          // Tier 1: inline form card (machinery.create_service_report, etc.)
          // Tier 2: modal confirmation (future)
          // Tier 3: blocked — never execute
          if (actionRequest.approval_tier === 0) {
            pendingUiActions.push(actionRequest);
          } else if (actionRequest.approval_tier === 3) {
            console.warn(`[SSE] Tier-3 (blocked) action received: ${actionRequest.action_type} — ignored`);
          } else {
            // Tier 1 and 2: attach to bot message as form card (tier 2 modal is future work)
            pendingActionRequest = actionRequest;
          }
        },
        onToken: (text) => {
          // Progressive text rendering — jitter-buffered for smooth delivery
          enqueueToken(text);
        },
        onThinking: (text) => {
          // MODEL-PICKER-11 (§14.2): thinking deltas render live in the
          // panel — dispatched straight to the origin thread's buffer (no
          // jitter buffer; the panel is auxiliary prose, not the answer).
          if (!text) return;
          thinkingAccum += text;
          dispatch(appendThinkingText({ text, threadId: streamTarget.current }));
        },
        onStats: (stats) => {
          // MODEL-PICKER-11 (§14.3): exactly one per honored pick — keep the
          // verbatim 13-field frame for the completed message. Absence (hard
          // error / un-honored pick) is normal; nothing is rendered then.
          turnStats = stats;
        },
        onLoopProgress: (frame) => {
          // V3-SEGMENT-STREAMING (SP-2): flush the jitter buffer FIRST so any
          // queued token text (which may include a marker line) lands in
          // streamingText BEFORE this frame's row update — otherwise a row that
          // arrived LATER on the wire would render ABOVE text that preceded it
          // (§10.2 wire-order violation). Frames are natural flush points.
          flushQueueImmediate();
          dispatch(applyLoopProgress({ frame, threadId: streamTarget.current }));
        },
        onThreadCreated: (tid) => {
          // MIDAS-SSE-THREAD: Early thread reconciliation — fires before any tokens stream.
          // Re-keys the thread buffer, sidebar entry, and active thread ID from the
          // optimistic temp ID to the real thread_id.
          receivedThreadId = tid;
          if (isNewThread && optimisticTempId) {
            // 1. Reconcile sidebar optimistic entry (midasPageSlice)
            import("../../customer/midas/midasPageSlice").then(
              ({ reconcileOptimisticThread }) => {
                dispatch(reconcileOptimisticThread({
                  tempId: optimisticTempId!,
                  realId: tid,
                }));
              }
            ).catch(() => {});
            // 2. Re-key stream buffer from temp ID to real thread ID (chatBotSlice)
            dispatch(reconcileStreamOrigin({
              requestId: streamId,
              oldThreadId: optimisticTempId,
              newThreadId: tid,
            }));
            // 3. Update the mutable stream target ref so jitter buffer writes to correct buffer
            originThreadId = tid;
            streamTarget.current = tid;
            earlyReconciled = true;
          }
          // Also handle popup→fullscreen reconciliation (same as existing onThreadId)
          if (originThreadId === POPUP_THREAD_ID && tid) {
            dispatch(reconcileStreamOrigin({
              requestId: streamId,
              oldThreadId: POPUP_THREAD_ID,
              newThreadId: tid,
            }));
            originThreadId = tid;
            streamTarget.current = tid;
            earlyReconciled = true;
            // Defense-in-depth: ensure activeThreadId is set so the component
            // reads from the correct buffer after reconciliation.
            import("../../customer/midas/midasPageSlice").then(
              ({ setActiveThreadId }) => {
                dispatch(setActiveThreadId(tid));
              }
            ).catch(() => {});
          }
        },
        onThreadId: (tid) => {
          // MIDAS-SSE-THREAD: thread_created already handled reconciliation — just record the ID
          if (receivedThreadId === tid) return;
          receivedThreadId = tid;
          // Fallback: if thread_created didn't fire (old bot version), reconcile here
          if (originThreadId === POPUP_THREAD_ID && tid) {
            dispatch(reconcileStreamOrigin({
              requestId: streamId,
              oldThreadId: POPUP_THREAD_ID,
              newThreadId: tid,
            }));
            originThreadId = tid;
            streamTarget.current = tid;
          }
        },
      });

      // Stream ended — flush all remaining characters immediately
      flushQueue();

      if (streamError) {
        // MIDAS-THREADSYNC Phase 2: Remove optimistic entry on error, trigger fallback refetch
        if (isNewThread && optimisticTempId) {
          import("../../customer/midas/midasPageSlice").then(
            ({ removeOptimisticThread }) => {
              dispatch(removeOptimisticThread({ tempId: optimisticTempId!, machineId: effectiveMachineId }));
            }
          ).catch(() => {});
          refreshSidebarFallback();
        }
        return rejectWithValue({
          error: streamError,
          originThreadId,
          streamId,
          surfaceGlobalError: isNewThread,
        });
      }

      if (!botMessage) {
        // MODEL-PICKER: same sync-fallback disable as the setup-failure path
        // above — an override turn (model_override) must never be silently
        // answered by the default model via the sync endpoint.
        if (isOverrideTurn) {
          return rejectWithValue({
            error: "No answer received from MIDAS",
            originThreadId,
            streamId,
            surfaceGlobalError: isNewThread,
          });
        }
        // Stream succeeded but no answer event — try sync fallback
        try {
          const syncResponse = await chatBotApi.sendMessage(
            message, effectiveMachineId, threadId, image, actionId, actionContext, actionResult, isNewThread
          );
          const syncBotMessage: ChatMessage = {
            id: generateId(),
            role: "assistant",
            text: syncResponse.message,
            timestamp: new Date().toISOString(),
            sources: mapBotSources(syncResponse.sources),
            suggestions: syncResponse.suggestions,
            intent: syncResponse.metadata?.intent,
            artifacts: mapBotArtifacts((syncResponse as any).artifacts),
            actions: mapBotActions((syncResponse as any).actions),
          };
          // Sync fallback path — check if response has thread_id
          if ((syncResponse as any).thread_id && typeof (syncResponse as any).thread_id === "string") {
            receivedThreadId = (syncResponse as any).thread_id;
          }
          reconcileOrRefetch();
          const deliveredToColumn = await deliverToColumnIfAttached(syncBotMessage);
          return { botMessage: syncBotMessage, originThreadId, streamId, deliveredToColumn };
        } catch {
          return rejectWithValue({
            error: "No answer received from MIDAS",
            originThreadId,
            streamId,
            surfaceGlobalError: isNewThread,
          });
        }
      }

      // MA-0b: If we received an action_request SSE event, attach it to the bot message.
      // The schema from the SSE drives form rendering (replaces MA-0a hardcoded schemas).
      if (pendingActionRequest && botMessage) {
        botMessage.actionRequest = pendingActionRequest;
      }

      // Attach tier-0 action requests (nav.switch_machine, nav.switch_platform, etc.) as chips
      if (pendingUiActions.length > 0 && botMessage) {
        botMessage.uiActionRequests = pendingUiActions;
      }

      // MODEL-PICKER-11: attach the turn's thinking transcript + stats frame
      // to the completed message (rides pendingBotMessage / attach-column
      // delivery unchanged — per-thread routing same as the answer).
      if (thinkingAccum && botMessage) {
        botMessage.thinkingText = thinkingAccum;
      }
      if (turnStats && botMessage) {
        botMessage.modelStats = turnStats;
      }

      // MIDAS-THREADSYNC Phase 2: Reconcile optimistic thread or fall back to refetch
      reconcileOrRefetch();

      // MODEL-PICKER P3: attached-column origin → append the bot message to
      // the column now; the fulfilled reducer will clear the buffer without
      // storing pendingBotMessage (see deliverToColumnIfAttached).
      const deliveredToColumn = await deliverToColumnIfAttached(botMessage);

      return { botMessage, originThreadId, streamId, deliveredToColumn };
    } catch (outerErr: unknown) {
      // Safety net: any uncaught error must still go through rejectWithValue
      // so that isLoading is properly reset in the .rejected handler
      // MIDAS-THREADSYNC Phase 2: Remove optimistic entry on unexpected error
      if (isNewThread && optimisticTempId) {
        import("../../customer/midas/midasPageSlice").then(
          ({ removeOptimisticThread }) => {
            dispatch(removeOptimisticThread({ tempId: optimisticTempId!, machineId: effectiveMachineId }));
          }
        ).catch(() => {});
      }
      const errMsg = deriveSendErrorMessage(
        outerErr,
        "An unexpected error occurred"
      );
      return rejectWithValue({
        error: errMsg,
        originThreadId,
        streamId,
        surfaceGlobalError: isNewThread,
      });
    }
  }
);

/** Fetch conversation history */
export const fetchHistory = createAsyncThunk(
  "chatBotSlice/fetchHistory",
  async (limit?: number) => {
    const response = await chatBotApi.getHistory(limit);

    // Map history messages to ChatMessage format (snake_case -> camelCase for sources)
    const messages: ChatMessage[] = response.messages.map((m) => ({
      id: generateId(),
      role: m.role,
      text: m.text,
      timestamp: m.timestamp,
      sources: mapBotSources(m.sources as any),
    }));

    return {
      messages,
      machineContext: response.machine_context
        ? {
            machineId: response.machine_context.machine_id,
            model: response.machine_context.model,
            serialNumber: response.machine_context.serial_number,
          }
        : null,
    };
  }
);

// ============================================================
// Enter/Exit Machine Mode Payload Types
// ============================================================

interface EnterMachinePayload {
  machineId: string;
  displayData: Partial<MachineContext>;
}

type SetMachineContextResult =
  | {
      action: "exit";
      savedMachineId: string | null;
      currentMessages: ChatMessage[];
      savedPlatformMessages: ChatMessage[];
    }
  | {
      action: "enter";
      context: MachineContext;
      currentMessages: ChatMessage[];
      savedMachineMessages: ChatMessage[];
    };

/** Set machine context for the chat session (pass null to exit machine mode) */
export const setMachineContext = createAsyncThunk(
  "chatBotSlice/setMachineContext",
  async (
    payload: EnterMachinePayload | null,
    { getState }
  ): Promise<SetMachineContextResult> => {
    const state = (getState() as { chatBot: ChatBotState }).chatBot;

    if (payload === null) {
      // EXIT machine mode — tell bot to clear context
      await chatBotApi.setMachineContext(null);
      return {
        action: "exit" as const,
        savedMachineId: state.machineContext?.machineId || null,
        currentMessages: [...state.messages],
        savedPlatformMessages: [...state.savedPlatformMessages],
      };
    }

    // ENTER machine mode — tell bot to set context (best-effort)
    let apiModel = "";
    let apiSerial = "";
    try {
      const response = await chatBotApi.setMachineContext(payload.machineId);
      apiModel = response.machine?.model || "";
      apiSerial = response.machine?.serial_number || "";
    } catch {
      // Bot may be offline — proceed with local data only
    }
    const context: MachineContext = {
      machineId: payload.machineId,
      model: apiModel || payload.displayData?.model || "",
      serialNumber: apiSerial || payload.displayData?.serialNumber || "",
      ...payload.displayData,
    };
    return {
      action: "enter" as const,
      context,
      currentMessages: [...state.messages],
      savedMachineMessages:
        state.savedMachineMessages[payload.machineId] || [],
    };
  }
);

/** Start a new chat — clears messages, context, and saved state; tells bot to exit machine mode if needed */
export const startNewChat = createAsyncThunk(
  "chatBotSlice/startNewChat",
  async (_, { getState }) => {
    const state = (getState() as { chatBot: ChatBotState }).chatBot;
    // If in machine mode, tell the bot to exit
    if (state.machineContext) {
      try {
        await chatBotApi.setMachineContext(null);
      } catch {
        // Best effort — clear local state even if API fails
      }
    }
  }
);

/** Check bot health */
export const checkHealth = createAsyncThunk(
  "chatBotSlice/checkHealth",
  async () => {
    const response = await chatBotApi.checkHealth();
    return response.status === "ok" || response.status === "healthy";
  }
);

/** Check if bot chat is enabled for this user/company */
export const checkBotAccess = createAsyncThunk(
  "chatBotSlice/checkBotAccess",
  async () => {
    const response = await chatBotApi.checkBotAccess();
    return response.enabled && response.globalEnabled;
  }
);

/** Update bot chat enabled setting for the current user's company */
export const updateBotChatSettings = createAsyncThunk(
  "chatBotSlice/updateBotChatSettings",
  async (enabled: boolean) => {
    const response = await chatBotApi.updateBotChatSettings(enabled);
    return response;
  }
);

// ============================================================
// Initial State
// ============================================================

const initialState: ChatBotState = {
  isOpen: false,
  messages: [],
  isLoading: false,
  currentStep: null,
  machineContext: null,
  machineContextLoading: false,
  isBotAvailable: false,
  isBotEnabled: false,
  botAccessChecked: false,
  hasUnread: false,
  error: null,
  showMachinePicker: false,
  streamingText: null,
  savedPlatformMessages: [],
  savedMachineMessages: {},
  pendingActions: {},
  executedActionIds: [],
  newThreadPending: false,
  threadBuffers: {},
  activeStreams: {},
};

// ============================================================
// Thread Buffer Helpers (MIDAS-SCOPESTREAM)
// ============================================================

const MAX_THREAD_BUFFERS = 20;

/** Create a fresh ThreadBuffer for a new stream. */
const createThreadBuffer = (): ThreadBuffer => ({
  streamingText: null,
  thinkingText: null,
  isLoading: true,
  currentStep: null,
  error: null,
  pendingBotMessage: null,
  lastActivity: Date.now(),
  // V3-SEGMENT-STREAMING (SP-2): transient loop_progress row state.
  liveRow: null,
  sawLoopProgress: false,
});

/** Evict oldest completed (non-loading) buffers when count exceeds MAX_THREAD_BUFFERS. */
const gcThreadBuffers = (buffers: Record<string, ThreadBuffer>): void => {
  const keys = Object.keys(buffers);
  if (keys.length <= MAX_THREAD_BUFFERS) return;
  // Collect completed (non-loading) buffers sorted by lastActivity ascending
  const completedEntries = keys
    .filter((k) => !buffers[k].isLoading)
    .map((k) => ({ key: k, activity: buffers[k].lastActivity }))
    .sort((a, b) => a.activity - b.activity);
  let toEvict = keys.length - MAX_THREAD_BUFFERS;
  for (const entry of completedEntries) {
    if (toEvict <= 0) break;
    delete buffers[entry.key];
    toEvict--;
  }
};

// ============================================================
// Slice
// ============================================================

const chatBotSlice = createSlice({
  name: "chatBotSlice",
  initialState,
  reducers: {
    /** Open the chat panel */
    openPanel: (state) => {
      state.isOpen = true;
      state.hasUnread = false;
    },
    /** Close the chat panel */
    closePanel: (state) => {
      state.isOpen = false;
    },
    /** Toggle the chat panel open/closed */
    togglePanel: (state) => {
      state.isOpen = !state.isOpen;
      if (state.isOpen) {
        state.hasUnread = false;
      }
    },
    /** Add a single message to the conversation */
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    /** Replace all messages (used when loading a thread) */
    setMessages: (state, action: PayloadAction<ChatMessage[]>) => {
      state.messages = action.payload;
    },
    /** Mark a user message as failed to send */
    markMessageFailed: (state, action: PayloadAction<string>) => {
      const msg = state.messages.find((m) => m.id === action.payload);
      if (msg) {
        msg.sendFailed = true;
      }
    },
    /** Set the current SSE progress step scoped to a thread buffer (MIDAS-SCOPESTREAM) */
    setCurrentStep: (
      state,
      action: PayloadAction<{ text: string | null; threadId: string }>
    ) => {
      const { text, threadId } = action.payload;
      const buffer = state.threadBuffers[threadId];
      if (buffer) {
        // V3-SEGMENT-STREAMING (SP-2): once loop_progress frames arrive on a v3
        // turn, the transient `searching` status line is suppressed — drop late
        // `onStep` dispatches (§10.1.6). Non-v3 turns never set this flag →
        // unchanged behavior.
        if (buffer.sawLoopProgress) return;
        buffer.currentStep = text;
      }
    },
    /** Set loading state (used when starting/ending SSE streaming) */
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    /** Append a token chunk to the origin thread's buffer (MIDAS-SCOPESTREAM) */
    appendStreamingToken: (
      state,
      action: PayloadAction<{ text: string; threadId: string }>
    ) => {
      const { text, threadId } = action.payload;
      const buffer = state.threadBuffers[threadId];
      if (buffer) {
        buffer.streamingText = (buffer.streamingText || "") + text;
        buffer.lastActivity = Date.now();
        // V3-SEGMENT-STREAMING (SP-2) dedupe: once a NEW marker line has
        // completed in the streaming text, the inline marker row (rendered by
        // the block splitter, in wire position) supersedes the transient
        // loop_progress frame row for that round — drop the frame row so we
        // never show both a settled frame-row AND a marker-row (marker wins,
        // contract rule). Only runs while a frame row is pending (guarded).
        if (buffer.liveRow) {
          const count = countMarkerLines(buffer.streamingText);
          if (count > buffer.liveRow.markerBaseline) {
            buffer.liveRow = null;
          }
        }
      }
    },
    /** MODEL-PICKER-11 (§14.2): append an `event: thinking` delta to the
     *  origin thread's buffer. Per-thread by construction → attach columns
     *  get their own thinking stream for free. */
    appendThinkingText: (
      state,
      action: PayloadAction<{ text: string; threadId: string }>
    ) => {
      const { text, threadId } = action.payload;
      const buffer = state.threadBuffers[threadId];
      if (buffer) {
        buffer.thinkingText = (buffer.thinkingText || "") + text;
        buffer.lastActivity = Date.now();
      }
    },
    /** V3-SEGMENT-STREAMING (SP-2): apply a `loop_progress` frame to the origin
     *  thread's transient live-row state. Defensive: never throws on an
     *  unexpected shape (the exact v2 JSON is an open [BOT-SIDE] pin —
     *  discrimination lives in the pure `classifyLoopProgressFrame`). */
    applyLoopProgress: (
      state,
      action: PayloadAction<{ frame: unknown; threadId: string }>
    ) => {
      const { frame, threadId } = action.payload;
      const buffer = state.threadBuffers[threadId];
      if (!buffer || !frame || typeof frame !== "object") return;

      // Any well-formed loop_progress frame suppresses the fabricated
      // `searching` status line (§10.1.6) — for ALL shapes incl. v1.
      buffer.sawLoopProgress = true;
      buffer.currentStep = null;

      const c = classifyLoopProgressFrame(frame);
      // Marker count in the CURRENT streaming text — the baseline a newly
      // opened/settled frame row is deduped against (see appendStreamingToken).
      const baseline = () => countMarkerLines(buffer.streamingText || "");

      if (c.type === "ignore") {
        // v1-shape / unrenderable — status already suppressed; render nothing.
        return;
      }

      if (c.type === "stage") {
        // Open or EXTEND the current unsettled live row (stages join with
        // " · ", consecutive duplicates deduped). A settled row is replaced by
        // a fresh live row (a new round started).
        const openLabels =
          buffer.liveRow && !buffer.liveRow.settled ? buffer.liveRow.labels : [];
        const label = c.label;
        const labels =
          label && openLabels[openLabels.length - 1] !== label
            ? [...openLabels, label]
            : openLabels;
        buffer.liveRow = {
          labels,
          settled: false,
          markerBaseline:
            buffer.liveRow && !buffer.liveRow.settled
              ? buffer.liveRow.markerBaseline
              : baseline(),
        };
        return;
      }

      if (c.type === "step_end") {
        // Settle the row with `label` (PROVISIONAL — replaced if the round's
        // marker line arrives, kept if prose resumes without one). ONLY settle
        // an EXISTING live row — never resurrect one. If the round's marker
        // already streamed in (the flush-on-frame drain can push it into
        // streamingText, clearing liveRow via the appendStreamingToken dedupe
        // BEFORE this frame runs), the inline marker row already represents the
        // step → do nothing, so we never show both (dedupe: marker wins).
        if (!buffer.liveRow) return;
        buffer.liveRow = {
          labels: buffer.liveRow.labels,
          settled: true,
          settledLabel: c.label ?? (buffer.liveRow.labels.join(" · ") || undefined),
          markerBaseline: buffer.liveRow.markerBaseline,
        };
        return;
      }

      // finalizing
      if (!c.render) {
        // reason clean / asking → render nothing; clear an unsettled live row.
        if (buffer.liveRow && !buffer.liveRow.settled) buffer.liveRow = null;
        return;
      }
      if (c.label) {
        buffer.liveRow = {
          labels: buffer.liveRow?.labels ?? [],
          settled: true,
          settledLabel: c.label,
          markerBaseline: buffer.liveRow?.markerBaseline ?? baseline(),
        };
      } else if (buffer.liveRow && !buffer.liveRow.settled) {
        // finalizing with no label + unknown reason → drop the live row.
        buffer.liveRow = null;
      }
    },
    /** Clear the streaming text (when answer arrives or on reset) */
    clearStreamingText: (state) => {
      state.streamingText = null;
    },
    /** Clear the hasUnread flag (when user opens panel) */
    clearUnread: (state) => {
      state.hasUnread = false;
    },
    /** Set hasUnread flag (when bot responds while panel is closed) */
    setUnread: (state) => {
      state.hasUnread = true;
    },
    /** Clear error */
    clearError: (state) => {
      state.error = null;
    },
    /**
     * Set machine context directly and save/restore messages (from "Chat about this machine" button).
     * This is the synchronous version used when entering machine mode without calling the API
     * (the button opens the panel, and the welcome screen handles the API call via setMachineContext thunk).
     */
    setMachineContextLocal: (
      state,
      action: PayloadAction<MachineContext | null>
    ) => {
      if (action.payload === null) {
        // EXIT: save machine messages, restore platform messages
        if (state.machineContext?.machineId) {
          state.savedMachineMessages[state.machineContext.machineId] =
            state.messages;
        }
        state.messages = state.savedPlatformMessages;
        state.savedPlatformMessages = [];
        state.machineContext = null;
      } else {
        // ENTER: save platform messages, restore or start fresh machine messages
        state.savedPlatformMessages = state.messages;
        const existingSaved =
          state.savedMachineMessages[action.payload.machineId];
        state.messages = existingSaved || [];
        if (existingSaved) {
          delete state.savedMachineMessages[action.payload.machineId];
        }
        state.machineContext = action.payload;
      }
    },
    /** Show the inline machine picker (replaces Dialog) */
    openMachinePicker: (state) => {
      state.showMachinePicker = true;
    },
    /** Hide the inline machine picker */
    closeMachinePicker: (state) => {
      state.showMachinePicker = false;
    },
    /** Set bot available status directly */
    setBotAvailable: (state, action: PayloadAction<boolean>) => {
      state.isBotAvailable = action.payload;
    },
    /** Set bot enabled status directly */
    setBotEnabled: (state, action: PayloadAction<boolean>) => {
      state.isBotEnabled = action.payload;
    },
    /** Mark an action button as clicked on a specific message (M7-D: action buttons) */
    markActionClicked: (
      state,
      action: PayloadAction<{ messageId: string; actionId: string }>
    ) => {
      const msg = state.messages.find((m) => m.id === action.payload.messageId);
      if (msg) {
        msg.clickedActionId = action.payload.actionId;
      }
    },
    /** MA-0: Set an action's state (pending/executing/success/error) */
    setActionStatus: (
      state,
      action: PayloadAction<ChatActionStatus>
    ) => {
      state.pendingActions[action.payload.actionId] = action.payload;
      // Track executed action IDs to prevent duplicates
      if (action.payload.state === "success" || action.payload.state === "error") {
        if (!state.executedActionIds.includes(action.payload.actionId)) {
          state.executedActionIds.push(action.payload.actionId);
        }
      }
    },
    /** Clear the new-thread pending flag (consumed by sendMessage after reading it) */
    clearNewThreadFlag: (state) => {
      state.newThreadPending = false;
    },
    /** Set the new-thread pending flag (used when starting a new chat without the full startNewChat thunk) */
    setNewThreadPending: (state) => {
      state.newThreadPending = true;
    },
    /** Register a new streaming buffer for the origin thread (MIDAS-SCOPESTREAM).
     *  Dispatched by sendMessage thunk after resolving originThreadId. */
    registerStreamBuffer: (
      state,
      action: PayloadAction<{ threadId: string; requestId: string }>
    ) => {
      const { threadId, requestId } = action.payload;
      state.threadBuffers[threadId] = createThreadBuffer();
      state.activeStreams[requestId] = threadId;
      gcThreadBuffers(state.threadBuffers);
    },
    /** Re-key a thread buffer from oldThreadId to newThreadId (MIDAS-SCOPESTREAM).
     *  Used when the SSE answer event returns a real thread_id, replacing a temp or "popup" key. */
    reconcileStreamOrigin: (
      state,
      action: PayloadAction<{
        requestId: string;
        oldThreadId: string;
        newThreadId: string;
      }>
    ) => {
      const { requestId, oldThreadId, newThreadId } = action.payload;
      // Re-key threadBuffers
      if (state.threadBuffers[oldThreadId] && oldThreadId !== newThreadId) {
        state.threadBuffers[newThreadId] = state.threadBuffers[oldThreadId];
        delete state.threadBuffers[oldThreadId];
      }
      // Re-key activeStreams
      if (state.activeStreams[requestId] === oldThreadId) {
        state.activeStreams[requestId] = newThreadId;
      }
    },
    /** Append a pending bot message from a completed background stream to state.messages.
     *  Dispatched by loadThread or MidasChatArea mount when checking for completed buffers. */
    appendPendingBotMessage: (
      state,
      action: PayloadAction<{ threadId: string }>
    ) => {
      const { threadId } = action.payload;
      const buffer = state.threadBuffers[threadId];
      if (buffer && buffer.pendingBotMessage) {
        state.messages.push(buffer.pendingBotMessage);
        buffer.pendingBotMessage = null;
        // GC: completed buffer, now active thread — delete it
        if (!buffer.isLoading) {
          delete state.threadBuffers[threadId];
        }
      }
    },
    /**
     * Set machine context directly without save/restore message swap and without API call.
     * Used by loadThread (restoring thread's machine context without wiping loaded messages)
     * and by machine card click (setting context synchronously to avoid race with welcome screen).
     */
    setMachineContextDirect: (state, action: PayloadAction<MachineContext | null>) => {
      state.machineContext = action.payload;
    },
    /** MA-0: Attach an action request to a chat message (for rendering MidasActionCard) */
    attachActionRequest: (
      state,
      action: PayloadAction<{ messageId: string; actionRequest: ChatActionRequest }>
    ) => {
      const msg = state.messages.find((m) => m.id === action.payload.messageId);
      if (msg) {
        msg.actionRequest = action.payload.actionRequest;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // sendMessage — MIDAS-SCOPESTREAM: Buffer creation is handled by registerStreamBuffer
      // dispatched from the thunk body. Pending handler only clears global error.
      .addCase(sendMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, { payload }) => {
        const { botMessage, originThreadId: origin, streamId: sid } = payload;
        const deliveredToColumn = (payload as any).deliveredToColumn === true;
        const buffer = state.threadBuffers[origin];
        if (buffer) {
          buffer.isLoading = false;
          buffer.streamingText = null;
          buffer.thinkingText = null;
          buffer.currentStep = null;
          // V3-SEGMENT-STREAMING (SP-2): clear transient loop_progress state —
          // the completed message renders marker-derived rows, never frame rows.
          buffer.liveRow = null;
          buffer.sawLoopProgress = false;
          buffer.lastActivity = Date.now();
        }
        // Remove from activeStreams
        delete state.activeStreams[sid];

        // MODEL-PICKER P3: the thunk already appended the bot message to its
        // attached column (midasAttachSlice.appendColumnMessage). Clear the
        // buffer WITHOUT storing pendingBotMessage — otherwise a later
        // loadThread of that thread would double-append the answer
        // (pendingBotMessage dedup, spec §6). No hasUnread: the column is
        // visible on screen.
        if (deliveredToColumn) {
          if (buffer) {
            delete state.threadBuffers[origin];
          }
          return;
        }
        // Determine if the origin thread is currently active (MIDAS) or popup
        // by checking midasPage.activeThreadId indirectly — we compare with the
        // thread buffer keys and the messages array context.
        // Heuristic: if the origin is POPUP_THREAD_ID, always push to messages
        // (popup doesn't switch threads). For MIDAS threads, check if origin matches
        // the thread whose messages are currently loaded by comparing against the
        // thread buffers — if a buffer with isLoading exists for a DIFFERENT thread,
        // the user may have switched. But the safest approach: always push to messages
        // if origin is the same as what the caller provided (the caller passed
        // activeThreadId at call time). If by the time fulfilled fires the user
        // has switched threads, the message will be in the wrong thread's messages array.
        //
        // The spec says: if origin !== activeThreadId, store as pendingBotMessage.
        // Since we can't read midasPage.activeThreadId from chatBotSlice reducers,
        // we use a simpler heuristic: if there are NO other active streams targeting
        // a different thread AND the origin buffer still exists, push to messages.
        // For background completions, the thunk already handles this via the streamId.
        //
        // Actually, we CAN detect: if the buffer was created but another thread's
        // buffer is currently loading, the user switched. But the spec's Option B says
        // components read from buffers directly. So we store the bot message as pending
        // and let the component decide. This is the safest approach.
        //
        // Simplified: store as pendingBotMessage in the buffer. The component (MidasChatArea
        // or ChatPanel) will call appendPendingBotMessage when it detects it.
        // Exception: popup always pushes directly (no thread switching in popup).
        if (origin === POPUP_THREAD_ID) {
          state.messages.push(botMessage);
          // GC popup buffer
          if (buffer) {
            delete state.threadBuffers[origin];
          }
        } else if (buffer) {
          buffer.pendingBotMessage = botMessage;
        } else {
          // No buffer (already GC'd?) — push directly as best effort
          state.messages.push(botMessage);
        }
        // If panel is closed, mark as unread
        if (!state.isOpen) {
          state.hasUnread = true;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        // MIDAS-SCOPESTREAM: Extract origin info from rejectWithValue payload
        const rejPayload = action.payload as
          | {
              error: string;
              originThreadId: string;
              streamId: string;
              surfaceGlobalError?: boolean;
            }
          | string
          | undefined;
        let errorMsg = "Failed to send message";
        let origin = POPUP_THREAD_ID;
        let sid = "";
        let surfaceGlobalError = false;
        if (rejPayload && typeof rejPayload === "object") {
          errorMsg = rejPayload.error || errorMsg;
          origin = rejPayload.originThreadId || origin;
          sid = rejPayload.streamId || sid;
          surfaceGlobalError = rejPayload.surfaceGlobalError === true;
        } else if (typeof rejPayload === "string") {
          errorMsg = rejPayload;
        } else if (action.error?.message) {
          errorMsg = action.error.message;
        }
        // Update the origin thread's buffer
        const buffer = state.threadBuffers[origin];
        if (buffer) {
          buffer.isLoading = false;
          buffer.streamingText = null;
          buffer.thinkingText = null;
          buffer.currentStep = null;
          // V3-SEGMENT-STREAMING (SP-2): clear transient loop_progress state.
          buffer.liveRow = null;
          buffer.sawLoopProgress = false;
          buffer.error = errorMsg;
          buffer.lastActivity = Date.now();
        }
        // Remove from activeStreams
        if (sid) {
          delete state.activeStreams[sid];
        }
        // Mark the last user message as failed (only if origin is popup —
        // for MIDAS threads the message will be in a different thread's history on next load)
        if (origin === POPUP_THREAD_ID) {
          const lastUserMsg = [...state.messages]
            .reverse()
            .find((m) => m.role === "user");
          if (lastUserMsg) {
            lastUserMsg.sendFailed = true;
          }
        }
        // MIDAS-404-SILENCE: a new-thread send that fails BEFORE the real
        // thread_id is reconciled leaves its error in an orphaned temp buffer
        // that no surface reads — the no-active-thread MIDAS chat reads the
        // GLOBAL error fallback (see ChatMessageList{,V2,V3}: `threadId ?
        // buffer.error : globalError`). Surface it globally so the ownership-
        // gate 404 renders a neutral message instead of total silence. The
        // popup passes threadId=POPUP_THREAD_ID (always truthy) so it reads the
        // buffer, never this global field — unaffected. Existing-thread and
        // column sends set surfaceGlobalError=false → no global write, no
        // stale-error leak to the welcome view. Transient: cleared by
        // sendMessage.pending and clearError.
        if (surfaceGlobalError) {
          state.error = errorMsg;
        }
      })
      // fetchHistory
      .addCase(fetchHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHistory.fulfilled, (state, { payload }) => {
        // ── TAJMAC-PILOT session-history guard ──
        //
        // `fetchHistory` restores the BOT SESSION's last conversation, and MidasChatAreaV3
        // dispatches it on cold load before any thread is chosen. The payload carries
        // `machineContext` only when that session was machine-scoped — it is null for a
        // platform (fleet-wide) session. Without this check a pilot whose last session was
        // platform-scoped would SEE that conversation rendered on load, which is the one
        // thing the role must never show.
        //
        // Guarding the WRITE rather than skipping the dispatch is deliberate: skipping
        // would also kill the pilot's legitimate machine-scoped session restore. Here the
        // legitimate case is exactly the case that still passes — `machineContext` present
        // means the session WAS machine-scoped, so those messages are restored unchanged.
        // Only the unscoped variant is dropped, and dropping it leaves the pilot on a
        // clean scoped surface (auto-scope + the thread list own their state).
        if (isMidasPilotUser() && !payload.machineContext) {
          state.messages = [];
          state.isLoading = false;
          return;
        }
        state.messages = payload.messages;
        if (payload.machineContext) {
          state.machineContext = payload.machineContext;
        }
        state.isLoading = false;
      })
      .addCase(fetchHistory.rejected, (state, { error }) => {
        state.isLoading = false;
        state.error = error.message || "Failed to load history";
      })
      // setMachineContext (enter or exit machine mode with context preservation)
      .addCase(setMachineContext.pending, (state) => {
        state.machineContextLoading = true;
      })
      .addCase(setMachineContext.fulfilled, (state, { payload }) => {
        state.machineContextLoading = false;
        state.showMachinePicker = false;

        if (payload.action === "exit") {
          // Save current machine messages
          if (payload.savedMachineId) {
            state.savedMachineMessages[payload.savedMachineId] =
              payload.currentMessages;
          }
          // Restore platform messages
          state.messages = payload.savedPlatformMessages;
          state.savedPlatformMessages = [];
          state.machineContext = null;
        } else if (payload.action === "enter") {
          // Save current platform messages
          state.savedPlatformMessages = payload.currentMessages;
          // Restore or start fresh machine messages
          state.messages = payload.savedMachineMessages;
          // Remove from saved (it's been restored)
          if (payload.context.machineId) {
            delete state.savedMachineMessages[payload.context.machineId];
          }
          state.machineContext = payload.context;
        }
      })
      .addCase(setMachineContext.rejected, (state, { error }) => {
        state.error = error.message || "Failed to set machine context";
        state.machineContextLoading = false;
        state.showMachinePicker = false;
      })
      // startNewChat — clear everything (API call is best-effort in the thunk)
      .addCase(startNewChat.pending, (state) => {
        state.messages = [];
        state.machineContext = null;
        state.machineContextLoading = false;
        state.currentStep = null;
        state.error = null;
        state.isLoading = false;
        state.showMachinePicker = false;
        state.streamingText = null;
        state.savedPlatformMessages = [];
        state.savedMachineMessages = {};
        state.pendingActions = {};
        state.executedActionIds = [];
        state.newThreadPending = true;
        // MIDAS-SCOPESTREAM: GC completed buffers (keep active streams running)
        for (const key of Object.keys(state.threadBuffers)) {
          if (!state.threadBuffers[key].isLoading) {
            delete state.threadBuffers[key];
          }
        }
      })
      // checkHealth
      .addCase(checkHealth.fulfilled, (state, { payload }) => {
        state.isBotAvailable = payload;
      })
      .addCase(checkHealth.rejected, (state) => {
        state.isBotAvailable = false;
      })
      // checkBotAccess
      .addCase(checkBotAccess.fulfilled, (state, { payload }) => {
        state.isBotEnabled = payload;
        state.botAccessChecked = true;
      })
      .addCase(checkBotAccess.rejected, (state) => {
        state.isBotEnabled = false;
        state.botAccessChecked = true;
      })
      // updateBotChatSettings
      .addCase(updateBotChatSettings.fulfilled, (state, { payload }) => {
        state.isBotEnabled = payload.enabled && payload.globalEnabled;
      });
  },
});

export const {
  openPanel,
  closePanel,
  togglePanel,
  addMessage,
  setMessages,
  markMessageFailed,
  setCurrentStep,
  setLoading,
  appendStreamingToken,
  appendThinkingText,
  applyLoopProgress,
  clearStreamingText,
  clearUnread,
  setUnread,
  clearError,
  setMachineContextLocal,
  openMachinePicker,
  closeMachinePicker,
  setBotAvailable,
  setBotEnabled,
  markActionClicked,
  setActionStatus,
  attachActionRequest,
  clearNewThreadFlag,
  setNewThreadPending,
  setMachineContextDirect,
  registerStreamBuffer,
  reconcileStreamOrigin,
  appendPendingBotMessage,
} = chatBotSlice.actions;

export default chatBotSlice.reducer;
